import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    IconX, IconRefresh, IconVolume, IconVolume3, IconKeyboard, IconKeyboardOff, IconAdjustmentsHorizontal, IconAlertTriangle,
} from '@tabler/icons-react';
import Keyboard from './Keyboard';
import ResultCard from './ResultCard';
import SettingsPanel, { TEXT_SIZES } from './SettingsPanel';
import { StrictRibbon, FreeSheet, FreeInputLine, RomanHint } from './TextDisplay';
import { FINGER_NAMES, fingerColor } from './fingers';
import { FINGER_OF } from '../engine/keyboardGeometry';
import { freeWordOnTrack } from '../typingEngine';

// Toolbar buttons must never take focus: a focused button would also "click" on
// Space or Enter while the user is typing.
const keepFocus = (e) => e.preventDefault();

function ToolButton({ onClick, label, active, children }) {
    return (
        <button
            type="button"
            onMouseDown={keepFocus}
            onClick={onClick}
            aria-label={label}
            title={label}
            className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${active
                ? 'bg-primary/10 text-primary'
                : 'text-[#7A726B] hover:bg-black/[0.05] hover:text-[#2C2722] dark:text-zinc-400 dark:hover:bg-white/[0.06] dark:hover:text-zinc-100'}`}
        >
            {children}
        </button>
    );
}

function Metric({ label, value, tone, wide }) {
    return (
        <div className={`flex flex-col items-center px-3 sm:px-4 ${wide ? 'min-w-[72px]' : 'min-w-[56px]'}`}>
            <span className={`text-[20px] font-bold leading-none tabular-nums sm:text-[22px] ${tone || 'text-[#1C1815] dark:text-zinc-100'}`}>{value}</span>
            <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#ABA39B] dark:text-zinc-500">{label}</span>
        </div>
    );
}

/**
 * The practice session, drawn over the whole window: a slim top bar with live
 * stats and quick controls, the text in the middle, and the guide keyboard below.
 * When the run ends the same window shows the result, so the typist never loses
 * their place. Keys are read by the page (a window listener), not by this view.
 */
export default function PracticeWindow({
    t, n, isBn,
    title, subtitle, icon: ModeIcon,
    phase, session, sessionKind, mode, layout, prefs, onPrefs,
    nextStroke, pressedCode, activeCodes, shake, imeWarning, wordSelected, onSelectWord,
    liveStats, clock, clockLabel, clockUrgent, started,
    settingsOpen, onToggleSettings, onRestart, onClose,
    result, resultProps,
}) {
    const { fit, short } = useScreenScale();
    const base = TEXT_SIZES[prefs.textSize] || TEXT_SIZES.md;
    const sizes = { strict: Math.round(base.strict * fit), sheet: Math.round(base.sheet * fit), input: Math.round(base.input * fit) };
    const panelRef = useRef(null);
    const mainRef = useRef(null);
    const colRef = useRef(null);
    const stageRef = useRef(null);
    const textRef = useRef(null);
    const kbCardRef = useRef(null);
    const [kbMaxH, setKbMaxH] = useState(null);

    // Lock the page behind the window while it is open.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    // Close the settings sheet on a click outside it.
    useEffect(() => {
        if (!settingsOpen) return undefined;
        const onDown = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest('[data-settings-toggle]')) onToggleSettings(false);
        };
        window.addEventListener('mousedown', onDown);
        return () => window.removeEventListener('mousedown', onDown);
    }, [settingsOpen, onToggleSettings]);

    const typing = phase === 'typing' && session;

    // The keyboard gets whatever height the window has left under the text, so the
    // whole session fits on one screen instead of pushing the bottom row out of view.
    // (Measured, not laid out with flex: the board's own size follows its scale.)
    useLayoutEffect(() => {
        const main = mainRef.current;
        const col = colRef.current;
        const stage = stageRef.current;
        const text = textRef.current;
        const card = kbCardRef.current;
        if (!main || !col || !stage || !text || !card) return undefined;
        const measure = () => {
            const colStyle = getComputedStyle(col);
            const gap = parseFloat(colStyle.rowGap) || 0;
            const shown = [...col.children].filter((el) => el.offsetHeight > 0);
            let used = parseFloat(colStyle.paddingTop) + parseFloat(colStyle.paddingBottom) + gap * (shown.length - 1);
            shown.forEach((el) => { used += el === stage ? text.offsetHeight + (parseFloat(getComputedStyle(stage).rowGap) || 0) : el.offsetHeight; });
            const cardStyle = getComputedStyle(card);
            const cardChrome = parseFloat(cardStyle.paddingTop) + parseFloat(cardStyle.paddingBottom) + 2;
            setKbMaxH(Math.max(140, Math.floor(main.clientHeight - used - cardChrome)));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(main);
        ro.observe(text);
        return () => ro.disconnect();
    }, [!!typing, prefs.showKeyboard]); // eslint-disable-line react-hooks/exhaustive-deps

    const finger = nextStroke ? (nextStroke.code === 'Space' ? 'th' : FINGER_OF[nextStroke.code]) : null;
    const progressPct = Math.round((liveStats?.progress || 0) * 100);

    return createPortal(
        <div
            className="fixed inset-0 z-[9000] flex flex-col bg-[#F7F5F2] animate-[popIn_0.18s_ease-out] dark:bg-zinc-950"
            role="dialog"
            aria-modal="true"
            aria-label={title}
        >
            {/* top bar */}
            <header className="relative flex-none border-b border-[#ECE7E2] bg-white/85 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/85">
                {/* Close sits in the window's top-right corner; the bar keeps room for it on the right. */}
                <div className="mx-auto flex h-[68px] max-w-[1280px] items-center gap-3 pl-3 pr-16 sm:pl-5 sm:pr-20">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                        <span className="hidden h-10 w-10 flex-none items-center justify-center rounded-xl bg-primary text-white shadow-[0_4px_12px_rgba(230,138,0,0.3)] sm:flex">
                            <ModeIcon size={21} stroke={1.8} />
                        </span>
                        <div className="min-w-0">
                            <div className="truncate font-hind text-[15px] font-bold leading-tight text-[#1C1815] dark:text-zinc-100">{title}</div>
                            {subtitle && <div className="truncate font-hind text-[12.5px] leading-tight text-[#8A837C] dark:text-zinc-400">{subtitle}</div>}
                        </div>
                    </div>

                    {typing && (
                        <div className="hidden items-center divide-x divide-[#EEE9E4] rounded-2xl border border-[#EEE9E4] bg-white py-2 dark:divide-zinc-800 dark:border-zinc-800 dark:bg-zinc-900 md:flex">
                            <Metric label={t('typingPractice.wpmShort')} value={n(Math.round(liveStats?.wpm || 0))} tone="text-primary" />
                            <Metric label={t('typingPractice.result.accuracy')} value={`${n(Math.round(liveStats?.acc ?? 100))}%`} />
                            <Metric label={clockLabel} value={clock} wide tone={clockUrgent ? 'text-red-500' : undefined} />
                        </div>
                    )}

                    <div className="flex flex-1 items-center justify-end gap-0.5">
                        <ToolButton onClick={() => onPrefs({ sound: !prefs.sound })} label={prefs.sound ? t('typingPractice.win.mute') : t('typingPractice.win.unmute')} active={prefs.sound}>
                            {prefs.sound ? <IconVolume size={20} /> : <IconVolume3 size={20} />}
                        </ToolButton>
                        <ToolButton onClick={() => onPrefs({ showKeyboard: !prefs.showKeyboard })} label={t('typingPractice.set.keyboard')} active={prefs.showKeyboard}>
                            {prefs.showKeyboard ? <IconKeyboard size={20} /> : <IconKeyboardOff size={20} />}
                        </ToolButton>
                        <span data-settings-toggle>
                            <ToolButton onClick={() => onToggleSettings(!settingsOpen)} label={t('typingPractice.settings')} active={settingsOpen}>
                                <IconAdjustmentsHorizontal size={20} />
                            </ToolButton>
                        </span>
                        {typing && (
                            <ToolButton onClick={onRestart} label={`${t('typingPractice.win.restart')} (Tab)`}><IconRefresh size={20} /></ToolButton>
                        )}
                    </div>
                </div>
                <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-3 sm:right-5">
                    <span className="h-6 w-px bg-[#ECE7E2] dark:bg-zinc-800" />
                    <button
                        type="button"
                        onMouseDown={keepFocus}
                        onClick={onClose}
                        aria-label={`${t('typingPractice.win.close')} (Esc)`}
                        title={`${t('typingPractice.win.close')} (Esc)`}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/35"
                    >
                        <IconX size={21} />
                    </button>
                </div>
                {/* progress line */}
                {typing && (
                    <div className="absolute inset-x-0 -bottom-px h-[3px] bg-transparent">
                        <div className="h-full rounded-r-full bg-primary transition-[width] duration-200" style={{ width: `${progressPct}%` }} />
                    </div>
                )}
            </header>

            {/* settings sheet */}
            {settingsOpen && (
                <div
                    ref={panelRef}
                    data-typing-ignore
                    className="absolute right-3 top-[76px] z-20 max-h-[calc(100vh-92px)] w-[min(360px,calc(100vw-24px))] overflow-y-auto rounded-2xl border border-[#ECE7E2] bg-white p-4 shadow-[0_18px_50px_rgba(28,24,21,0.18)] animate-[popIn_0.15s_ease-out] dark:border-zinc-800 dark:bg-zinc-900 sm:right-5"
                >
                    <SettingsPanel prefs={prefs} onChange={onPrefs} t={t} n={n} />
                </div>
            )}

            {/* body */}
            <main ref={mainRef} className="flex-1 overflow-y-auto">
                {typing ? (
                    <div ref={colRef} className="mx-auto flex min-h-full w-full flex-col gap-4 px-4 py-4 sm:px-8 sm:py-6" style={{ maxWidth: Math.round(1120 * fit) }}>
                        {/* stats for small screens */}
                        <div className="flex items-center justify-center divide-x divide-[#EEE9E4] rounded-2xl border border-[#EEE9E4] bg-white py-2 dark:divide-zinc-800 dark:border-zinc-800 dark:bg-zinc-900 md:hidden">
                            <Metric label={t('typingPractice.wpmShort')} value={n(Math.round(liveStats?.wpm || 0))} tone="text-primary" />
                            <Metric label={t('typingPractice.result.accuracy')} value={`${n(Math.round(liveStats?.acc ?? 100))}%`} />
                            <Metric label={clockLabel} value={clock} wide tone={clockUrgent ? 'text-red-500' : undefined} />
                        </div>

                        {imeWarning && (
                            <div className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-[13px] font-semibold text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-400">
                                <IconAlertTriangle size={17} className="flex-none" /> {t('typingPractice.imeWarning')}
                            </div>
                        )}

                        <div ref={stageRef} className="flex flex-1 flex-col justify-center gap-4">
                            <section ref={textRef} className="rounded-[28px] border border-[#ECE7E2] bg-white px-5 py-6 shadow-[0_1px_3px_rgba(28,24,21,0.04),0_12px_32px_-12px_rgba(28,24,21,0.10)] dark:border-zinc-800 dark:bg-zinc-900 sm:px-10 sm:py-7">
                                {sessionKind === 'strict' ? (
                                    <StrictRibbon session={session} shake={shake} size={sizes.strict} />
                                ) : (
                                    <div className="flex flex-col gap-4">
                                        <FreeSheet session={session} sheetLook={mode === 'exam'} size={sizes.sheet} lines={short ? 2 : 3} />
                                        <FreeInputLine session={session} isBn={isBn} selected={wordSelected} onSelectAll={onSelectWord} size={sizes.input} offTrack={!freeWordOnTrack(session, layout)} raw={layout.rawText ? layout.rawText(session.buffers[session.index] || []) : null} />
                                    </div>
                                )}
                                <div className="mt-4 flex min-h-[30px] flex-wrap items-center justify-center gap-x-4 gap-y-2">
                                    {!started && (
                                        <span className="rounded-full bg-primary/10 px-3 py-1 text-[12.5px] font-semibold text-primary">
                                            {sessionKind === 'free' ? t('typingPractice.win.timerHint') : t('typingPractice.win.strictHint')}
                                        </span>
                                    )}
                                    {layout.phonetic && mode !== 'exam' && (
                                        <RomanHint session={session} layout={layout} kind={sessionKind} />
                                    )}
                                    {prefs.fingerHint && finger && (
                                        <span className="flex items-center gap-2 rounded-full border border-[#EEE9E4] px-3 py-1 dark:border-zinc-800">
                                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: fingerColor(finger) }} />
                                            <span className="text-[12.5px] font-semibold text-[#6B635C] dark:text-zinc-400">{FINGER_NAMES[isBn ? 'bn' : 'en'][finger]}</span>
                                        </span>
                                    )}
                                </div>
                            </section>

                            {prefs.showKeyboard && (
                                <div ref={kbCardRef} className="rounded-[28px] border border-[#ECE7E2] bg-[#FBFAF8] p-3 dark:border-zinc-800 dark:bg-zinc-900/60 sm:p-4">
                                    <Keyboard
                                        legend={layout.legend}
                                        nextStroke={nextStroke}
                                        pressedCode={pressedCode}
                                        showFingerColors={prefs.fingerColors}
                                        dimInactive={mode === 'learn' && prefs.dimKeys}
                                        activeCodes={activeCodes}
                                        maxScale={1.15 * fit}
                                        maxHeight={kbMaxH}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 pb-1 text-[11.5px] font-medium text-[#ABA39B] dark:text-zinc-500">
                            <span><Key>Esc</Key> {t('typingPractice.win.close')}</span>
                            <span><Key>Tab</Key> {t('typingPractice.win.restart')}</span>
                            {sessionKind === 'free' && <span><Key>Ctrl</Key>+<Key>A</Key> {t('typingPractice.win.selectWord')}</span>}
                        </div>
                    </div>
                ) : result ? (
                    <div className="mx-auto w-full px-4 py-6 sm:px-8 sm:py-10" style={{ maxWidth: Math.round(820 * fit) }}>
                        <ResultCard {...resultProps} />
                    </div>
                ) : null}
            </main>
        </div>,
        document.body,
    );
}

/**
 * How much bigger than the base design the practice area should draw: 1 up to a
 * 1280px-wide window, growing with wider screens to 1.5×. The window height caps it
 * too, so text and keyboard still fit on a short wide screen.
 */
function useScreenScale() {
    const read = () => {
        if (typeof window === 'undefined') return { fit: 1, short: false };
        const fit = Math.min(1.5, Math.max(1, Math.min(window.innerWidth / 1280, window.innerHeight / 780)));
        // A short window (laptop) shows two lines of passage instead of three, so the keyboard keeps its size.
        return { fit, short: window.innerHeight < 800 };
    };
    const [screen, setScreen] = useState(read);
    useEffect(() => {
        const onResize = () => setScreen(read());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return screen;
}

const Key = ({ children }) => (
    <kbd className="rounded-md border border-[#DDD6CF] bg-white px-1.5 py-px font-sans text-[10.5px] font-bold text-[#7A726B] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">{children}</kbd>
);
